import { assign, fromPromise, setup } from "xstate";
import type { components } from "../index";

import { HTTP_FORBIDDEN, HTTP_UNAUTHORIZED } from "../constants/common";
import type { Session } from "../types/auth";
import { cloneSession } from "@fleet/auth";
import type { TokenStorage } from "../powersync/base-connector";

export const SESSION_PAYLOAD_KEY = "session_payload";
export const POWERSYNC_JWT_CACHE_KEY = "powersync_jwt_cache";
export const POWERSYNC_JWT_EXPIRES_KEY = "powersync_jwt_expires";

/**
 * List of all keys used by the session machine for persistence.
 * Useful for performing synchronous "hard clears" during logout.
 */
export const SESSION_STORAGE_KEYS = [
  SESSION_PAYLOAD_KEY,
  POWERSYNC_JWT_CACHE_KEY,
  POWERSYNC_JWT_EXPIRES_KEY,
] as const;

export type SessionEvent =
  | {
      type: "SERVER_REPLY";
      session: Session | null;
      error: unknown;
    }
  | { type: "LOGOUT" }
  | { type: "VERIFY_COMPLETE" };

export interface SessionContext {
  session: Session | null;
  isPrimed: boolean;
  isVerified: boolean;
  isVisitor: boolean;
  error: components["schemas"]["ErrorModel"] | null;
  /**
   * Ids of the sessions that LOGOUT revoked. A get-session that was in flight
   * when LOGOUT ran can resolve afterwards with one of them; such a reply must
   * not put the session back. A sign-in creates a new session id, so it is
   * still accepted.
   */
  revokedSessionIds: string[];
  storage: TokenStorage;
  appScheme?: string;
}

export interface SessionMachineInput {
  storage: TokenStorage;
  appScheme?: string;
}

/**
 * Handles the persistent identity lifecycle of the application.
 * Framework-agnostic version that expects a TokenStorage implementation.
 */
export const sessionMachine = setup({
  // Schema Definition Pattern for XState v5 type inference
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  types: {} as {
    context: SessionContext;
    events: SessionEvent;
    input: SessionMachineInput;
  },
  actors: {
    primeSession: fromPromise(
      async ({ input }: { input: SessionMachineInput }): Promise<Session | null> => {
        try {
          const payload = await input.storage.getItem(SESSION_PAYLOAD_KEY);
          if (payload) {
            const parsed = JSON.parse(payload) as unknown;
            if (isValidSession(parsed)) {
              return parsed;
            }
          }
        } catch (error) {
          console.warn("[sessionMachine] Priming error:", error);
        }
        return null;
      },
    ),
    persistSession: fromPromise(
      async ({
        input,
      }: {
        input: { session: Session | null; storage: TokenStorage; appScheme?: string };
      }) => {
        try {
          if (input.session) {
            // Clone the payload to remove the sensitive session token before persisting
            const safePayload = cloneSession(input.session);
            if (safePayload.session.token) {
              safePayload.session.token = "REDACTED";
            }
            const payload = JSON.stringify(safePayload);
            await input.storage.setItem(SESSION_PAYLOAD_KEY, payload);
          } else {
            // Cleanup
            const keys = [SESSION_PAYLOAD_KEY, "powersync_jwt_cache", "powersync_jwt_expires"];

            if (input.appScheme) {
              keys.push(`${input.appScheme}_session_token`);
            }

            await Promise.allSettled(
              keys.map(async (key) => {
                try {
                  await input.storage.setItem(key, "");
                } catch {
                  // Ignore
                }
              }),
            );
          }
        } catch (error) {
          console.warn("[sessionMachine] Persist error:", error);
        }
      },
    ),
  },
}).createMachine({
  id: "session",
  initial: "priming",
  context: ({ input }) => ({
    session: null,
    isPrimed: false,
    isVerified: false,
    isVisitor: false,
    error: null,
    revokedSessionIds: [],
    storage: input.storage,
    appScheme: input.appScheme,
  }),
  states: {
    priming: {
      invoke: {
        src: "primeSession",
        input: ({ context }) => ({
          storage: context.storage,
          appScheme: context.appScheme,
        }),
        onDone: {
          target: "ready",
          actions: assign({
            session: ({ event }) => event.output,
            isPrimed: true,
            isVisitor: ({ event }) => !event.output,
          }),
        },
        onError: {
          target: "ready",
          actions: assign({
            isPrimed: true,
            isVisitor: true,
          }),
        },
      },
    },
    ready: {
      type: "parallel",
      states: {
        monitoring: {
          on: {
            SERVER_REPLY: {
              guard: ({ context, event }) => !isRevokedReply(context, event),
              actions: assign({
                session: ({ context, event }) => {
                  if (event.session) {
                    return event.session;
                  }
                  if (
                    isStatusError(event.error) &&
                    (event.error.status === HTTP_UNAUTHORIZED ||
                      event.error.status === HTTP_FORBIDDEN)
                  ) {
                    return null;
                  }
                  return context.session;
                },
                error: ({ event }) => (isErrorModel(event.error) ? event.error : null),
                isVerified: true,
                isVisitor: ({ context, event }) => {
                  if (event.session) {
                    return false;
                  }
                  // If we already have a session, we're not a visitor unless
                  // the server explicitly rejected the credentials.
                  if (
                    isStatusError(event.error) &&
                    (event.error.status === HTTP_UNAUTHORIZED ||
                      event.error.status === HTTP_FORBIDDEN)
                  ) {
                    return true;
                  }
                  return !context.session;
                },
              }),
            },
            VERIFY_COMPLETE: {
              actions: assign({ isVerified: true }),
            },
            LOGOUT: {
              actions: assign({
                session: null,
                isVisitor: true,
                isVerified: true,
                error: null,
                revokedSessionIds: ({ context }) => {
                  const id = sessionIdOf(context.session);
                  return id === null
                    ? context.revokedSessionIds
                    : [...context.revokedSessionIds, id];
                },
              }),
            },
          },
        },
        persisting: {
          on: {
            SERVER_REPLY: {
              guard: ({ context, event }) => {
                if (isRevokedReply(context, event)) {
                  return false;
                }
                const isAuthError =
                  isStatusError(event.error) &&
                  (event.error.status === HTTP_UNAUTHORIZED ||
                    event.error.status === HTTP_FORBIDDEN);
                return Boolean(event.session) || isAuthError;
              },
              target: ".updating",
            },
            LOGOUT: {
              target: ".updating",
            },
          },
          initial: "idle",
          states: {
            idle: {},
            updating: {
              invoke: {
                src: "persistSession",
                input: ({ context, event }) => ({
                  session: event.type === "LOGOUT" ? null : context.session,
                  storage: context.storage,
                  appScheme: context.appScheme,
                }),
                onDone: "idle",
                onError: "idle",
              },
            },
          },
        },
      },
    },
  },
});

// ── Helpers ──────────────────────────────────────────────────────────────────

function sessionIdOf(session: Session | null): string | null {
  const id: unknown = session?.session.id;
  return typeof id === "string" ? id : null;
}

/** A reply that carries a session LOGOUT already revoked (a stale get-session). */
function isRevokedReply(context: SessionContext, event: SessionEvent): boolean {
  if (event.type !== "SERVER_REPLY" || !event.session) {
    return false;
  }
  const id = sessionIdOf(event.session);
  return id !== null && context.revokedSessionIds.includes(id);
}

export function isValidSession(value: unknown): value is Session {
  if (!value || typeof value !== "object") {
    return false;
  }

  const v = value as unknown;
  if (!v || typeof v !== "object" || !("user" in v) || !("session" in v)) {
    return false;
  }

  const { user } = v as { user: unknown };
  const sessionObj = (v as { session: unknown }).session;

  const isValidUser =
    user &&
    typeof user === "object" &&
    "id" in user &&
    typeof (user as { id: unknown }).id === "string";

  const isValidSessionObj =
    sessionObj &&
    typeof sessionObj === "object" &&
    "token" in sessionObj &&
    typeof (sessionObj as { token: unknown }).token === "string";

  return Boolean(isValidUser && isValidSessionObj);
}

export function isStatusError(error: unknown): error is { status: number } {
  if (typeof error !== "object" || error === null) {
    return false;
  }
  return "status" in error && typeof error.status === "number";
}

export function isErrorModel(error: unknown): error is components["schemas"]["ErrorModel"] {
  if (typeof error !== "object" || error === null) {
    return false;
  }
  return "message" in error && typeof error.message === "string";
}
